import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { FileText, Clock, CheckCircle2, XCircle, CalendarClock, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import RefundRequestModal from "@/components/RefundRequestModal";

const REFUND_WINDOW_DAYS = 14; // matches backend default — buyers see the button until then

function AvailabilityBadge({ order, lang }) {
  const s = order.availability_status;
  if (!s) return null;
  const deadline = order.availability_deadline;
  const now = new Date();
  const dl = deadline ? new Date(deadline) : null;
  const hoursLeft = dl ? Math.max(0, (dl - now) / 36e5) : 0;

  const config = {
    pending_availability: {
      icon: <Clock className="w-4 h-4"/>,
      label: lang === "en" ? "Awaiting stock check" : "En attente de confirmation",
      cls: "bg-orange-50 text-orange-800 border-orange-200",
    },
    available: {
      icon: <CheckCircle2 className="w-4 h-4"/>,
      label: lang === "en" ? "Confirmed available" : "Disponible — confirmée",
      cls: "bg-green-50 text-green-800 border-green-200",
    },
    out_of_stock: {
      icon: <XCircle className="w-4 h-4"/>,
      label: lang === "en" ? "Out of stock" : "Produit épuisé",
      cls: "bg-red-50 text-red-800 border-red-200",
    },
    delayed: {
      icon: <CalendarClock className="w-4 h-4"/>,
      label: order.expected_available_date
        ? (lang === "en" ? `Available from ${order.expected_available_date}` : `Disponible à partir du ${order.expected_available_date}`)
        : (lang === "en" ? "Available later" : "Disponible plus tard"),
      cls: "bg-yellow-50 text-yellow-800 border-yellow-200",
    },
  }[s];
  if (!config) return null;
  return (
    <div className={`mt-3 p-3 rounded-lg border flex items-start gap-2 ${config.cls}`} data-testid={`availability-${s}`}>
      {config.icon}
      <div className="flex-1">
        <p className="text-sm font-bold">{config.label}</p>
        {s === "pending_availability" && dl && hoursLeft > 0 && (
          <p className="text-[11px] mt-0.5">
            {lang === "en" ? "Verification within" : "Vérification sous"} {hoursLeft < 1 ? `${Math.round(hoursLeft * 60)} min` : `${hoursLeft.toFixed(1)}h`}
          </p>
        )}
        {order.availability_note && (
          <p className="text-[11px] mt-0.5 italic opacity-80">{order.availability_note}</p>
        )}
      </div>
    </div>
  );
}

function RefundBadge({ refund, t }) {
  if (!refund) return null;
  const cls = {
    pending: "bg-amber-50 text-amber-900 border-amber-200",
    approved: "bg-green-50 text-green-900 border-green-200",
    rejected: "bg-red-50 text-red-900 border-red-200",
  }[refund.status] || "bg-neutral-50 text-neutral-800 border-neutral-200";
  const labelKey = {
    pending: "refund.badge_pending",
    approved: "refund.badge_approved",
    rejected: "refund.badge_rejected",
  }[refund.status];
  return (
    <div className={`mt-3 p-3 rounded-lg border ${cls}`} data-testid={`refund-status-${refund.status}`}>
      <p className="text-sm font-bold">{t(labelKey)}</p>
      {refund.status === "approved" && (
        <p className="text-[11px] mt-1">💰 {t("refund.approved_hint")}</p>
      )}
      {refund.status === "rejected" && refund.rejection_reason && (
        <p className="text-[11px] mt-1"><strong>{t("refund.rejected_reason")}</strong> {refund.rejection_reason}</p>
      )}
    </div>
  );
}

/**
 * The refund button appears only for paid orders inside the 14-day window
 * that don't already have a pending/approved refund request. The 14-day
 * cutoff mirrors backend enforcement — showing an unclickable button after
 * that would just confuse the buyer.
 */
function canRequestRefund(order) {
  if (order.payment_status !== "paid") return false;
  if (order.status === "refunded") return false;
  const req = order.refund_request;
  if (req && (req.status === "pending" || req.status === "approved")) return false;
  const paidAt = order.paid_at || order.created_at;
  if (!paidAt) return true;
  const paidDate = new Date(paidAt);
  if (isNaN(paidDate.getTime())) return true;
  const days = (Date.now() - paidDate.getTime()) / 86400000;
  return days <= REFUND_WINDOW_DAYS;
}

export default function Orders() {
  const { user, loading } = useAuth();
  const { t, lang } = useLang();
  const [orders, setOrders] = useState([]);
  const [refundOrder, setRefundOrder] = useState(null);

  const STATUS_LABELS = {
    pending: t("orders.pending"), confirmed: t("orders.confirmed"), shipped: t("orders.shipped"),
    delivered: t("orders.delivered"), cancelled: t("orders.cancelled"), refunded: lang === "en" ? "Refunded" : "Remboursée",
  };

  useEffect(() => {
    if (user) api.get("/orders/mine").then((r) => setOrders(r.data));
  }, [user]);

  const handleRefundDone = (fresh) => {
    setOrders((prev) => prev.map((o) => (o.id === fresh.id ? fresh : o)));
  };

  if (loading) return null;
  if (!user) return <Navigate to="/connexion"/>;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="orders-page">
      <h1 className="heading font-extrabold text-4xl mb-8">{t("orders.title")}</h1>
      {orders.length === 0 ? (
        <p className="text-[#4B5563]">{t("orders.empty")} <Link to="/produits" className="text-[#C85A32] underline">{t("home.all_products")}</Link></p>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.id} className="bg-white border border-[#1A3322]/10 rounded-lg p-5" data-testid={`order-${o.id}`}>
              <div className="flex justify-between">
                <div>
                  <p className="font-semibold">{t("orders.reference")} #{o.id.slice(0, 8)}</p>
                  <p className="text-xs text-[#4B5563]">{new Date(o.created_at).toLocaleString(lang === "en" ? "en-US" : "fr-FR")}</p>
                </div>
                <div className="text-right">
                  <p className="heading font-extrabold text-xl text-[#1A3322]">{o.total.toFixed(2)} €</p>
                  <span className="text-xs uppercase tracking-wider bg-[#F5F5F0] px-2 py-1 rounded font-semibold">{STATUS_LABELS[o.status] || o.status}</span>
                </div>
              </div>
              <ul className="mt-3 text-sm space-y-1">
                {o.items.map((it) => <li key={`${o.id}-${it.kind}-${it.item_id}`}>· {it.title} × {it.quantity}</li>)}
              </ul>
              <AvailabilityBadge order={o} lang={lang}/>
              <RefundBadge refund={o.refund_request} t={t} />
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1A3322]/5 flex-wrap gap-2">
                <p className="text-xs text-[#4B5563]">{t("orders.payment")}: <span className="font-medium">{o.payment_status}</span>{o.provider && <> · via <span className="font-medium">{o.provider}</span></>}</p>
                <div className="flex gap-2 flex-wrap">
                  {o.provider === "bank_transfer" && o.payment_status !== "paid" && (
                    <Link to={`/virement/${o.id}`} className="text-xs text-[#C85A32] font-semibold underline" data-testid={`view-transfer-${o.id}`}>{t("orders.awaiting_transfer")} →</Link>
                  )}
                  {o.payment_status === "paid" && (
                    <Link to={`/facture/${o.id}`} className="inline-flex items-center gap-1 text-xs text-[#1A3322] font-semibold underline" data-testid={`view-invoice-${o.id}`}><FileText className="w-3 h-3"/>{t("orders.invoice")}</Link>
                  )}
                  {canRequestRefund(o) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRefundOrder(o)}
                      className="rounded-full text-xs h-7 px-3 border-[#C85A32]/40 text-[#C85A32] hover:bg-[#FFF7ED]"
                      data-testid={`request-refund-${o.id}`}
                    >
                      <RotateCcw className="w-3 h-3 mr-1"/>{t("refund.request_cta")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {refundOrder && (
        <RefundRequestModal
          order={refundOrder}
          onClose={() => setRefundOrder(null)}
          onDone={handleRefundDone}
        />
      )}
    </div>
  );
}
